const loaded = new Map<string, Promise<void>>();

/**
 * Load a third-party picker SDK once, on demand. Nothing is fetched until the
 * user actually clicks that provider, so the page costs nothing to open.
 */
export function loadScript(src: string, attributes: Record<string, string> = {}): Promise<void> {
  const existing = loaded.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    for (const [key, value] of Object.entries(attributes)) {
      script.setAttribute(key, value);
    }
    script.onload = () => resolve();
    script.onerror = () => {
      loaded.delete(src);
      reject(new Error('Could not load ' + src));
    };
    document.head.appendChild(script);
  });

  loaded.set(src, promise);
  return promise;
}

/** Poll for an SDK global that appears slightly after its script fires onload. */
export function waitFor<T>(get: () => T | undefined, timeoutMs = 10_000): Promise<T> {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      const value = get();
      if (value) return resolve(value);
      if (Date.now() - started > timeoutMs) return reject(new Error('Timed out waiting for the picker to load'));
      setTimeout(tick, 50);
    };
    tick();
  });
}
